// Reference: https://rsuitejs.com/components/date-range-picker/
import React, { MutableRefObject } from 'react';
import RSuiteDateRangePicker, { DateRange } from 'rsuite/DateRangePicker';
import koreanLocale from 'rsuite/locales/ko_KR';
import styled, { createGlobalStyle } from 'styled-components';
import colors from '@/common/view/colors';
import typescales from '@/common/view/typescales';
import { startOfDay, endOfDay, startOfMonth, endOfMonth, addMonths } from 'date-fns';

export type Size = 'small' | 'medium';
export type PickerPlacement = 'bottomStart' | 'bottomEnd' | 'topStart'; //'topStart' | 'topEnd' | 'bottomStart' | 'bottomEnd';

const pickerLocale = {
  ...koreanLocale.DateRangePicker,
  ok: '적용',
};

const { beforeToday } = RSuiteDateRangePicker;

const reviseMenuPostitionX = (
  hasIcon: boolean,
  iconPosition: 'left' | 'right',
  placement?: PickerPlacement,
): string => {
  if (placement === 'bottomStart') {
    if (hasIcon && iconPosition === 'left') {
      return '-40px';
    }
    return '0px';
  } else if (placement === 'bottomEnd') {
    if (hasIcon && iconPosition === 'left') {
      return '16px';
    }
    return '36px';
  } else {
    if (hasIcon && iconPosition === 'left') {
      return '-36px';
    }
    return '-12px';
  }
};

const GlobalStyle = createGlobalStyle<{
  hasLeftIcon?: boolean;
  $hasIcon: boolean;
  $iconPosition: 'left' | 'right';
  $placement?: PickerPlacement;
}>`
  :root {
    --rs-input-focus-border: transparent;
    --rs-calendar-range-bg: ${colors.blue20};
    --rs-listbox-option-hover-bg: transparent;
    --rs-listbox-option-hover-text: inherit;
    --rs-calendar-range-bg: ${colors.blue20};
    --rs-text-secondary: ${colors.gray40};
    --rs-picker-value: ${colors.grayBlack};
  }
  
  .rs-picker-popup-daterange {
    transform: translate(${({ $hasIcon, $iconPosition, $placement }) =>
      reviseMenuPostitionX($hasIcon, $iconPosition, $placement)} , ${({ $placement }) =>
  $placement === 'topStart' ? '-12px' : '12px'} );
  }
  .rs-picker-daterange-panel {
    .rs-picker-daterange-content {
      .rs-picker-daterange-header {
        ${({ theme }) => theme.typescales.body4};
      }
      .rs-picker-daterange-calendar-group {
        height: 285px;
        .rs-calendar {
          height: 285px;
          
          .rs-calendar-header {
            .rs-calendar-header-month-toolbar {
              button.rs-calendar-header-backward,
              button.rs-calendar-header-forward {
                margin-top: 2px;
              }
              button.rs-calendar-header-title {
                ${typescales.body3};
              color: ${colors.grayBlack};
              }
            }
          }

          .rs-calendar-body {
            padding-top: 12px;
            .rs-calendar-table {
              .rs-calendar-table-header-cell {
                ${typescales.caption2};
                ${colors.gray50};
                height: 32px;
              }
              .rs-calendar-table-cell {
                ${typescales.body5};
                padding: 0;
                .rs-calendar-table-cell-content {
                  border-radius: 8px;
                }
                &:hover {
                  .rs-calendar-table-cell-content {
                    box-shadow: inset 0 0 0 2px ${colors.blue50};
                  }
                }
                
                &.rs-calendar-table-cell-in-range {
                  &:before {
                    margin-top: 0;
                    height: 30px;
                  }
                  &:hover {
                    background-color: ${colors.blue20};
                    .rs-calendar-table-cell-content {
                    }
                  }
                }

                &.rs-calendar-table-cell-selected {
                  &:before {
                    margin-top: 0;
                    height: 100%;
                  }
                  &.rs-calendar-table-cell-selected-start {
                    .rs-calendar-table-cell-content {
                      width: 100%;
                      height: 100%;
                      border-top-right-radius: 0;
                      border-bottom-right-radius: 0;
                      background-color: ${colors.blue50};
                    }
                    &.rs-calendar-table-cell-selected-end {
                      .rs-calendar-table-cell-content {
                        border-radius: 8px;
                      }
                    }
                  }
                  &.rs-calendar-table-cell-selected-end {
                    .rs-calendar-table-cell-content {
                      width: 100%;
                      height: 100%;
                      border-top-left-radius: 0;
                      border-bottom-left-radius: 0;
                      background-color: ${colors.blue50};
                    }
                  }
                }
                    
                &.rs-calendar-table-cell-is-today {
                  color: ${colors.orange50};
                }
              }
            }
          }
        }
      }
    }
    .rs-picker-toolbar {
      padding: 12px 12px 6px;
      ${({ theme }) => theme.typescales.body4};
      .rs-picker-toolbar-right {
        button {
          padding: 6px 18px;
          ${({ theme }) => theme.typescales.body4};
          background-color: ${({ theme }) => theme.colors.blue50};

        }
      }
    }
  }
`;

const Wrapper = styled.div<{
  $size: Size;
  $selected: boolean;
  $selectedTextColor?: string;
  $width?: string;
}>`
  display: flex;
  white-space: nowrap;
  align-items: center;
  width: ${({ $selected, $width }) => $width || ($selected ? '185px' : '130px')};

  & > svg {
    flex: 0 0 18px;
    path {
      fill: ${({ theme }) => theme.colors.gray40};
    }
  }

  .rs-picker.rs-picker-daterange {
    display: inline-flex;
    align-items: center;
    position: relative;
    &.rs-picker-toggle-wrapper {
      display: inline-flex;

      .rs-input-group {
        border: none;
        input.rs-input.rs-date-range-input {
          padding: 0;
          ${({ $size, theme }) => ($size === 'small' ? theme.typescales.body4 : theme.typescales.body1)}
        }
        span.rs-input-group-addon {
          display: none;
        }

        &.rs-input-group-inside {
        }
      }
      .rs-input-group:focus-within,
      .rs-input-group:not(.rs-input-group-disabled).rs-input-group-focus {
        outline: none;
      }

      /* Legacy */
      .rs-picker-toggle {
        display: inline-flex;
        justify-content: start;
        padding: 0 !important;
        svg.rs-picker-toggle-caret {
          display: none;
        }
        .rs-picker-toggle-clean {
          display: none;
        }
        .rs-picker-toggle-textbox {
          padding: 0;
          border: none;
          ${({ $size, theme }) => ($size === 'small' ? theme.typescales.body4 : theme.typescales.body1)}
        }
        &.rs-picker-toggle-active {
          box-shadow: none;
          background-color: transparent;
        }
        &:hover {
          background-color: transparent;
        }
        &:focus {
          background-color: transparent;
        }
      }

      &.rs-picker-has-value {
        .rs-picker-toggle .rs-picker-toggle-value {
          color: ${({ $selectedTextColor }) => $selectedTextColor || colors.grayBlack};
        }
      }
    }

    &.rs-picker-disabled {
      opacity: 0.6;
      &.rs-picker-toggle-wrapper {
        &.rs-picker-has-value {
          .rs-picker-toggle .rs-picker-toggle-value {
            color: ${({ theme }) => theme.colors.gray50};
          }
        }
      }
    }
  }
`;

export type PickerHandler = { open: (() => void) | undefined; close: (() => void) | undefined };

export interface DateRangePickerProps {
  value?: DateRange | null;
  onChange?: (value: DateRange | null) => void;
  placeholder?: string;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  iconPosition?: 'left' | 'right';
  inputRef?: MutableRefObject<HTMLInputElement | null>;
  selectedTextColor?: string;
  placement?: PickerPlacement;
  disabled?: boolean;
  width?: string;
  size?: Size;
  disablePastDates?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  placeholder,
  Icon,
  iconPosition = 'left',
  inputRef,
  selectedTextColor,
  placement,
  disabled,
  width,
  size = 'small',
  disablePastDates = false,
}) => {
  return (
    <Wrapper $size={size} $selected={!!value} $selectedTextColor={selectedTextColor} $width={width}>
      {Icon && iconPosition === 'left' && <Icon width={18} height={18} style={{ marginRight: '6px' }} />}

      <RSuiteDateRangePicker
        locale={pickerLocale}
        appearance="subtle"
        editable={false}
        disabled={disabled}
        placeholder={placeholder}
        placement={placement}
        value={value || null}
        ranges={[
          {
            label: '오늘',
            value: [startOfDay(new Date()), endOfDay(new Date())],
          },
          {
            label: '이번 달',
            value: [startOfMonth(new Date()), endOfMonth(new Date())],
          },
          {
            label: '지난 달 ~ 이번 달',
            value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(new Date())],
          },
        ]}
        ref={(instance) => {
          if (instance?.target && inputRef) {
            const inputEl = instance?.target.querySelector('input.rs-picker-toggle-textbox');
            inputRef.current = inputEl as HTMLInputElement;
          }
        }}
        character={' ~ '}
        format="yyyy.MM.dd"
        onChange={onChange}
        shouldDisableDate={disablePastDates ? beforeToday() : undefined}
      />
      {Icon && iconPosition === 'right' && <Icon width={18} height={18} style={{ marginLeft: '6px' }} />}
      <GlobalStyle $hasIcon={!!Icon} $iconPosition={iconPosition} $placement={placement} />
    </Wrapper>
  );
};

export default DateRangePicker;
